import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Page, useAccountTimeZone, useOrgName, useShellStatus, useTerms } from '../../layout';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { ExportMenu } from '../../components/shared/ExportMenu';
import { PDF_SUMMARY } from '../PlaceholderPage';
import { Badge, ClassificationTag, SeverityLabel, SYNTHETIC_DATA_LABEL, SyntheticDataBadge } from '../../components/shared/Badges';
import { Card, Delta, Kpi, KpiLedger } from '../../components/shared/Card';
import { Dot } from '../../components/shared/Badges';
import { EmptyState, ErrorState, Skeleton } from '../../components/shared/States';
import { BarChart, HBars, LineChart, Sparkline, useTableToggle } from '../../components/shared/charts';
import {
  analyticsApi,
  dayCount,
  dayLabel,
  hourLabel,
  hourRange,
  priorGap,
  rangeText,
  seriesValues,
  severityKey,
  slaSentence,
  useAnalytics,
  whenText,
} from '../../services/analyticsApi';

const NODE_COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)', 'var(--series-4)', 'var(--series-5)', 'var(--series-6)', 'var(--series-7)', 'var(--series-8)'];

const pct = (v) => Number(v).toFixed(1);

function deltaPoints(current, previous, lowerIsBetter = true) {
  const d = Number(current) - Number(previous);
  if (Math.abs(d) < 0.05) return { direction: 'flat', text: '0.0 pts' };
  const up = d > 0;
  return { direction: up ? (lowerIsBetter ? 'up-bad' : 'up-good') : lowerIsBetter ? 'down-good' : 'down-bad', text: `${Math.abs(d).toFixed(1)} pts` };
}

function deltaSeconds(current, previous) {
  const d = current - previous;
  if (Math.abs(d) <= 3) return { direction: 'flat', text: fmt.mmss(Math.abs(d)) };
  return { direction: d > 0 ? 'up-bad' : 'down-good', text: fmt.mmss(Math.abs(d)) };
}

function KpiRow({ data, gap }) {
  const t = data.totals;
  const p = gap ? null : data.previous;
  const target = data.target;
  const series = data.series || {};
  const spark = (key) => <Sparkline values={seriesValues(series[key])} width={56} />;

  let callsMeta = `${fmt.int(t.answeredCalls)} answered`;
  let callsDelta = null;
  if (p) {
    const change = ((t.totalCalls - p.totalCalls) / p.totalCalls) * 100;
    callsDelta = <Delta direction={change >= 0 ? 'up-good' : 'down-good'}>{`${Math.abs(change).toFixed(1)}%`}</Delta>;
    callsMeta = `${fmt.int(t.answeredCalls)} answered, `;
  } else if (gap) {
    callsMeta = `${fmt.int(t.answeredCalls)} answered, ${gap.toLowerCase()}`;
  }

  const abandonOver = t.abandonRate >= Number(target.abandonTargetPercent);
  const slaBelow = t.serviceLevelPercent < Number(target.answerTargetPercent);

  return (
    <KpiLedger>
      <Kpi label="Calls offered" value={<span data-testid="kpi-total-calls">{fmt.int(t.totalCalls)}</span>} spark={spark('totalCalls')} meta={<>{callsMeta}{callsDelta}</>} />
      <Kpi
        label="Abandon rate"
        value={pct(t.abandonRate)}
        unit="%"
        spark={spark('abandonRate')}
        meta={
          abandonOver ? (
            <Badge tone="critical" icon="alert">{`Over ${Number(target.abandonTargetPercent)}% target`}</Badge>
          ) : p ? (
            <>
              <Delta direction={deltaPoints(t.abandonRate, p.abandonRate).direction}>{deltaPoints(t.abandonRate, p.abandonRate).text}</Delta> vs prior
            </>
          ) : (
            `target under ${Number(target.abandonTargetPercent)}%`
          )
        }
      />
      <Kpi
        label="SLA met"
        value={pct(t.serviceLevelPercent)}
        unit="%"
        spark={spark('serviceLevelPercent')}
        meta={
          slaBelow ? (
            <Badge tone="critical" icon="alert">{`Below ${Number(target.answerTargetPercent)}% target`}</Badge>
          ) : (
            <Badge tone="good" icon="check">{`Target ${Number(target.answerTargetPercent)}%`}</Badge>
          )
        }
      />
      <Kpi
        label="Average wait"
        value={fmt.mmss(t.averageWaitSeconds)}
        spark={spark('averageWaitSeconds')}
        meta={
          p ? (
            <>
              <Delta direction={deltaSeconds(t.averageWaitSeconds, p.averageWaitSeconds).direction}>{deltaSeconds(t.averageWaitSeconds, p.averageWaitSeconds).text}</Delta> vs prior
            </>
          ) : (
            gap
          )
        }
      />
      <Kpi
        label="Average handle time"
        value={fmt.mmss(t.averageHandleSeconds)}
        spark={spark('averageHandleSeconds')}
        meta={
          p ? (
            <>
              <Delta direction={deltaSeconds(t.averageHandleSeconds, p.averageHandleSeconds).direction}>{deltaSeconds(t.averageHandleSeconds, p.averageHandleSeconds).text}</Delta> vs prior
            </>
          ) : (
            gap
          )
        }
      />
      <Kpi
        label="Negative sentiment"
        value={pct(t.negativeSentimentPercent)}
        unit="%"
        spark={spark('negativeSentimentPercent')}
        meta={data.includesSyntheticData ? `of analyzed calls · ${SYNTHETIC_DATA_LABEL}` : 'of analyzed calls'}
      />
    </KpiLedger>
  );
}

function HourCard({ data, compare, previousLabel }) {
  const table = useTableToggle();
  const { label: zoneLabel } = useAccountTimeZone();
  const current = data.callsByHour.map((h) => Number(h.calls));
  const previous = (data.previousCallsByHour || []).map((h) => Number(h.calls));
  const hours = hourRange(current.map((v, h) => v || previous[h]));
  const lines = [{ name: 'This period', values: hours.map((h) => current[h] || 0), slot: 1 }];
  if (compare && data.previousCallsByHour) lines.push({ name: previousLabel, values: hours.map((h) => previous[h] || 0), color: 'var(--series-other)', dashed: true });
  return (
    <Card className="c-8" title="Calls by hour of day" description={`Average per weekday, ${zoneLabel}`} actions={table.button}>
      <LineChart
        labels={hours.map(hourLabel)}
        xName="Hour"
        series={lines}
        area
        height={236}
        aria="Average calls by hour of day"
        vFmt={(v) => `${fmt.int(v)} calls`}
        showTable={table.on}
      />
    </Card>
  );
}

function DivisionCard({ data, selectedId }) {
  const target = Number(data.target.answerTargetPercent);
  const nodes = data.byHierarchy.filter((n) => n.nodeId);
  return (
    <Card
      className="c-4"
      bodyClassName="div-rows small"
      title={`Calls by ${data.topLevelName}`}
      description="Share of offered calls, with SLA met"
      footer={slaSentence(data.target, data.inBusinessHoursOnly)}
    >
      {nodes.map((n, i) => {
        const below = n.daysWithCalls > 0 && n.serviceLevelPercent < target;
        const why = below && n.daysBelowTarget > 0 ? `SLA below target on ${n.daysBelowTarget} of ${n.daysWithCalls} days` : null;
        return (
          <div key={n.nodeId} className="div-row" style={{ opacity: !selectedId || selectedId === n.nodeId ? 1 : 0.45 }}>
            <span className="div-name">
              <Dot color={NODE_COLORS[i % NODE_COLORS.length]} />
              <strong>{n.name}</strong>
            </span>
            <div className="meter" title={`SLA met ${n.serviceLevelPercent}%`}>
              <div className="fill" style={{ width: `${n.serviceLevelPercent}%`, background: below ? 'var(--critical-mark)' : 'var(--series-1)' }} />
              <div className="target" style={{ left: `${target}%` }} />
            </div>
            {why ? (
              <span className="div-warn" role="img" title={why} aria-label={why}>
                <Icon name="alert" className="ico-sm" />
              </span>
            ) : (
              <span />
            )}
            <span className="div-sla num">{n.daysWithCalls > 0 ? `${pct(n.serviceLevelPercent)}%` : '—'}</span>
            <span className="div-calls num">
              {fmt.int(n.totalCalls)} <span className="muted">calls</span>
            </span>
          </div>
        );
      })}
    </Card>
  );
}

function DailyCard({ data, selectedId }) {
  const table = useTableToggle();
  const nodes = data.byHierarchy.map((n, i) => ({ ...n, color: NODE_COLORS[i % NODE_COLORS.length] })).filter((n) => n.nodeId && (!selectedId || n.nodeId === selectedId));
  return (
    <Card className="c-7" title="Daily volume" description={`Offered calls by ${data.topLevelName}, ${rangeText(data.from, data.to)}`} actions={table.button}>
      <BarChart
        labels={data.days.map(dayLabel)}
        xName="Day"
        stacked
        height={236}
        aria={`Daily offered calls by ${data.topLevelName}`}
        series={nodes.map((n) => ({ name: n.name, values: seriesValues(n.series.totalCalls), color: n.color }))}
        showTable={table.on}
      />
    </Card>
  );
}

function ConcernsCard({ data }) {
  return (
    <Card
      className="c-5"
      title="Top concerns"
      description={`Calls by classification, AI-analyzed calls only (${fmt.int(data.totals.analyzedCalls)} of ${fmt.int(data.totals.totalCalls)})`}
      actions={
        <>
          {data.includesSyntheticData && <SyntheticDataBadge />}
          <Link className="btn btn-ghost btn-sm" to="/insights">
            Open Insights
          </Link>
        </>
      }
    >
      {data.topConcerns.length ? (
        <HBars
          labelWidth="1.5fr"
          items={data.topConcerns.map((c) => ({
            label: c.categoryName,
            value: c.calls,
            node: (
              <Link to={`/calls?categoryIds=${encodeURIComponent(c.categoryId)}`} style={{ color: 'var(--text)' }}>
                {c.categoryName}
              </Link>
            ),
          }))}
        />
      ) : (
        <div className="caption">No analyzed calls in this period yet.</div>
      )}
    </Card>
  );
}

function ScorecardCard({ data, onSelect }) {
  const target = Number(data.target.answerTargetPercent);
  const rows = data.byHierarchy.filter((n) => n.nodeId).slice().sort((a, b) => b.totalCalls - a.totalCalls);
  const colorOf = Object.fromEntries(data.byHierarchy.map((n, i) => [n.nodeId, NODE_COLORS[i % NODE_COLORS.length]]));
  const columns = [
    { key: 'name', header: data.topLevelName },
    { key: 'totalCalls', header: 'Calls' },
    { key: 'abandonRate', header: 'Abandoned' },
    { key: 'serviceLevelPercent', header: 'SLA met' },
    { key: 'averageWaitSeconds', header: 'Avg wait', exportValue: (r) => fmt.mmss(r.averageWaitSeconds) },
    { key: 'negativeSentimentPercent', header: 'Negative' },
    { key: 'topCategoryName', header: 'Top classification' },
  ];
  return (
    <Card
      className="c-8"
      flush
      data-testid="division-scorecard"
      title={`${data.topLevelName} scorecard`}
      description="Sorted by offered calls"
      actions={
        <ExportMenu anchor="card" name={`${data.topLevelName} scorecard`} columns={columns} rows={rows} />
      }
    >
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{data.topLevelName}</th>
              <th className="n">Calls</th>
              <th className="n">Abandoned</th>
              <th className="n">SLA met</th>
              <th className="n">Avg wait</th>
              <th className="n">Negative</th>
              <th>Top classification</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((n) => (
              <tr key={n.nodeId} className="clickable" onClick={() => onSelect(n.nodeId)}>
                <td>
                  <span className="row" style={{ gap: 8 }}>
                    <Dot color={colorOf[n.nodeId]} />
                    <span className="cell-title">{n.name}</span>
                  </span>
                </td>
                <td className="n">{fmt.int(n.totalCalls)}</td>
                <td className="n">{pct(n.abandonRate)}%</td>
                <td className="n">
                  {n.daysWithCalls > 0 && n.serviceLevelPercent < target ? (
                    <span className="sev sev-immediate">
                      <Icon name="alert" />
                      {pct(n.serviceLevelPercent)}%
                    </span>
                  ) : n.daysWithCalls > 0 ? (
                    `${pct(n.serviceLevelPercent)}%`
                  ) : (
                    '—'
                  )}
                </td>
                <td className="n">{fmt.mmss(n.averageWaitSeconds)}</td>
                <td className="n">{pct(n.negativeSentimentPercent)}%</td>
                <td>{n.topCategoryName ? <ClassificationTag category={n.topCategoryName} severity="Standard" /> : <span className="caption">No analyzed calls</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function AttentionCard({ data, label }) {
  return (
    <Card
      className="c-4"
      flush
      title="Needs attention"
      description="Immediate and Within 48 Hours classifications"
      actions={
        <Link className="btn btn-ghost btn-sm" to="/insights#escalations">
          View all {data.attentionTotal}
        </Link>
      }
    >
      {data.attention.length ? (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {data.attention.map((a) => (
            <li key={a.callRecordId} style={{ padding: '11px 16px', borderTop: '1px solid var(--divider)' }}>
              <Link to={`/calls/${a.callRecordId}`} style={{ display: 'block', color: 'inherit', textDecoration: 'none' }}>
                <div className="row between">
                  <SeverityLabel severity={severityKey(a.severity)} />
                  <span className="caption">{whenText(a.startTime)}</span>
                </div>
                <div className="small strong" style={{ marginTop: 4 }}>
                  {a.categoryName}
                  {a.subcategoryName ? ` › ${a.subcategoryName}` : ''}
                </div>
                {a.summaryShort && <div className="small sub">{a.summaryShort}</div>}
                <div className="caption" style={{ marginTop: 2 }}>
                  {[a.communityName, a.nodeName].filter(Boolean).join(', ')}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="caption" style={{ padding: 16 }}>
          Nothing urgent for {label}.
        </div>
      )}
    </Card>
  );
}

function OverviewSkeleton() {
  return (
    <div className="stack-lg">
      <Skeleton height={114} />
      <div className="grid">
        <div className="c-8">
          <Skeleton height={360} />
        </div>
        <div className="c-4">
          <Skeleton height={360} />
        </div>
      </div>
    </div>
  );
}

export function OverviewPage() {
  const org = useOrgName();
  const h = useHierarchyFilter();
  const terms = useTerms();
  const [compare, setCompare] = useState(true);
  const days = dayCount(h.range.from, h.range.to);
  const previousLabel = `Previous ${days} ${days === 1 ? 'day' : 'days'}`;
  const key = JSON.stringify([h.params, compare]);
  const { data, error, loading, retry } = useAnalytics(() => analyticsApi.overview(h.params, compare), key);
  const filtered = Boolean(h.filter.nodeId || h.filter.communityIds.length);
  const { status: shell } = useShellStatus();
  const gap = data ? priorGap({ from: h.range.from, to: h.range.to, historyStartDate: shell?.callHistoryStartDate, hasPrior: data.previous?.totalCalls > 0 }) : null;

  const filters = useMemo(
    () => (
      <label className="switch small" style={{ marginLeft: 4 }}>
        <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} /> Compare to previous {days} {days === 1 ? 'day' : 'days'}
      </label>
    ),
    [compare, days]
  );

  let body;
  if (error) {
    body = <ErrorState title="Overview couldn't load" body="Vertolytics couldn't reach the analytics service. Figures on this page are unavailable until it responds." error={error} onRetry={retry} />;
  } else if (!data) {
    body = <OverviewSkeleton />;
  } else if (data.totals.totalCalls === 0) {
    body = (
      <EmptyState
        icon="phone"
        title="No calls in this period yet"
        body={`Call records arrive from Microsoft Teams about 15 minutes after each call ends. If ${org} just connected its Microsoft 365 organization, the historical import may still be running.`}
      />
    );
  } else {
    body = (
      <div className="stack-lg" aria-busy={loading || undefined}>
        {filtered && (
          <div className="alert alert-info">
            <Icon name="filter" />
            <div>
              Showing <strong>{h.label}</strong>. Calls are attributed to {terms.aCommunity} by queue or auto attendant mapping first, then by the caller's own words when a queue serves several {terms.communitiesLower}.
            </div>
          </div>
        )}
        <KpiRow data={data} gap={gap} />
        <div className="grid">
          <HourCard data={data} compare={compare} previousLabel={previousLabel} />
          <DivisionCard data={data} selectedId={h.filter.nodeId} />
          <DailyCard data={data} selectedId={h.filter.nodeId} />
          <ConcernsCard data={data} />
          <ScorecardCard data={data} onSelect={(id) => h.setDivision(id)} />
          <AttentionCard data={data} label={h.label} />
        </div>
      </div>
    );
  }

  return (
    <Page
      title="Overview"
      subtitle={`How ${org}'s phones performed for the selected period, across every queue and auto attendant.`}
      exportConfig={{ name: 'Overview', endpoint: '/export/datasets/DailyMetrics', params: { title: 'Overview' }, extraFormats: PDF_SUMMARY('Board-ready one-page summary (.pdf)') }}
      filters={filters}
    >
      {body}
    </Page>
  );
}
